import React from 'react'
import { format } from 'date-fns'
import type {
  ContextualMenuItem,
  FileCardAction,
  FileCardKind,
} from '@enamelai/design-system'
import {
  Icon as ClaimsIcon,
  src as claimsSrc,
} from '@enamelai/design-system/icons/File_2'
import {
  Icon as DeleteIcon,
  src as deleteSrc,
} from '@enamelai/design-system/icons/Delete1'
import {
  Icon as DownloadIcon,
  src as downloadSrc,
} from '@enamelai/design-system/icons/Download'
import {
  Icon as EditIcon,
  src as editSrc,
} from '@enamelai/design-system/icons/Edit'
import {
  Icon as FilesIcon,
  src as filesSrc,
} from '@enamelai/design-system/icons/Files'
import {
  Icon as InfoIcon,
  src as infoSrc,
} from '@enamelai/design-system/icons/Info'
import {
  Icon as MoveIcon,
  src as moveSrc,
} from '@enamelai/design-system/icons/Move_to_folder_2'
import {
  Icon as NotesIcon,
  src as notesSrc,
} from '@enamelai/design-system/icons/Drafts'
import {
  Icon as ViewIcon,
  src as viewSrc,
} from '@enamelai/design-system/icons/View'

export type FileTypeKind = FileCardKind

const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'svg',
  'tif',
  'tiff',
]

export function getFileTypeFromFileName(fileName: string): FileTypeKind {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? ''
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image'
  if (ext === 'pdf') return 'pdf'
  if (ext === 'dcm') return 'dcm'
  if (['doc', 'docx'].includes(ext)) return 'document'
  return 'other'
}

export function getFileIconClass(kind: FileTypeKind): string {
  if (kind === 'image') return 'mgc_pic_fill'
  if (kind === 'pdf') return 'mgc_pdf_fill'
  return 'mgc_file_fill'
}

export function getFileNameWithoutExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot <= 0) return fileName
  return fileName.slice(0, lastDot)
}

export function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot <= 0) return ''
  return fileName.slice(lastDot)
}

export function formatFileDate(dateStr: string): string {
  if (!dateStr) return ''
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    return format(date, 'MMM d, yyyy - h:mm a')
  } catch {
    return ''
  }
}

export type FileMenuHandlers = {
  onView?: () => void
  onViewWithDicom?: () => void
  onDownload: () => void
  onNotes?: () => void
  onDetails?: () => void
  onRename?: () => void
  onMove?: () => void
  onMakeCopy?: () => void
  onAttachToClaims?: () => void
  onDelete: () => void
}

export function buildFileActions(handlers: FileMenuHandlers): FileCardAction[] {
  const actions: FileCardAction[] = [
    {
      id: 'view',
      label: 'View file',
      icon: 'mgc_eye_2_line',
      hideForKinds: ['dcm'],
      onClick: handlers.onView,
    },
  ]
  if (handlers.onViewWithDicom) {
    actions.push({
      id: 'dicom',
      label: 'Open with DICOM',
      icon: 'mgc_expand_player_line',
      whenKinds: ['image', 'dcm'],
      onClick: handlers.onViewWithDicom,
    })
  }
  actions.push(
    {
      id: 'download',
      label: 'Download',
      icon: 'mgc_download_2_line',
      onClick: handlers.onDownload,
    },
    {
      id: 'notes',
      label: 'Notes',
      icon: 'mgc_document_line',
      onClick: handlers.onNotes,
    },
    {
      id: 'details',
      label: 'Details',
      icon: 'mgc_information_line',
      onClick: handlers.onDetails,
    },
    {
      id: 'rename',
      label: 'Rename',
      icon: 'mgc_edit_2_line',
      onClick: handlers.onRename,
    },
    {
      id: 'move',
      label: 'Move',
      icon: 'mgc_folder_upload_line',
      onClick: handlers.onMove,
    },
    {
      id: 'copy',
      label: 'Make a copy',
      icon: 'mgc_documents_line',
      onClick: handlers.onMakeCopy,
    },
    {
      id: 'claims',
      label: 'Attach to claims',
      icon: 'mgc_document_2_line',
      onClick: handlers.onAttachToClaims,
    },
    {
      id: 'delete',
      label: 'Delete file',
      icon: 'mgc_delete_2_line',
      tone: 'danger',
      onClick: handlers.onDelete,
    },
  )
  return actions
}

export type FolderMenuHandlers = {
  onDownloadZip?: () => void
  onDetails?: () => void
  onMakeCopy?: () => void
  onRename?: () => void
  onMove?: () => void
  onDelete?: () => void
}

export function buildFolderActions(
  handlers: FolderMenuHandlers,
): FileCardAction[] {
  return [
    {
      id: 'zip',
      label: 'Download Zip',
      icon: 'mgc_download_2_line',
      onClick: handlers.onDownloadZip,
    },
    {
      id: 'details',
      label: 'Details',
      icon: 'mgc_information_line',
      onClick: handlers.onDetails,
    },
    {
      id: 'copy',
      label: 'Make a copy',
      icon: 'mgc_documents_line',
      onClick: handlers.onMakeCopy,
    },
    {
      id: 'rename',
      label: 'Rename',
      icon: 'mgc_edit_2_line',
      onClick: handlers.onRename,
    },
    {
      id: 'move',
      label: 'Move',
      icon: 'mgc_folder_upload_line',
      onClick: handlers.onMove,
    },
    {
      id: 'delete',
      label: 'Delete folder',
      icon: 'mgc_delete_2_line',
      tone: 'danger',
      onClick: handlers.onDelete,
    },
  ]
}

type SvgIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>

const MENU_ICONS: Record<string, SvgIcon> = {
  view: ViewIcon,
  download: DownloadIcon,
  zip: DownloadIcon,
  notes: NotesIcon,
  details: InfoIcon,
  rename: EditIcon,
  move: MoveIcon,
  copy: FilesIcon,
  claims: ClaimsIcon,
  delete: DeleteIcon,
}

const MENU_ICON_SRCS: Record<string, string> = {
  view: viewSrc,
  download: downloadSrc,
  zip: downloadSrc,
  notes: notesSrc,
  details: infoSrc,
  rename: editSrc,
  move: moveSrc,
  copy: filesSrc,
  claims: claimsSrc,
  delete: deleteSrc,
}

/**
 * The DS FileCard only accepts icon class names, so DS icons are drawn as CSS
 * masks. Its menu is portaled to <body>, so the mask URLs live on :root.
 */
if (typeof document !== 'undefined') {
  Object.entries(MENU_ICON_SRCS).forEach(([id, src]) => {
    document.documentElement.style.setProperty(
      `--fal-menu-icon-${id}`,
      `url("${src}")`,
    )
  })
}

export function withMaskMenuIcons(actions: FileCardAction[]): FileCardAction[] {
  return actions.map((action) =>
    MENU_ICON_SRCS[action.id]
      ? { ...action, icon: `fal-menu-icon fal-menu-icon--${action.id}` }
      : action,
  )
}

/** Same filtering and danger styling the DS FileCard applies to its own menu. */
export function toContextualMenuItems(
  actions: FileCardAction[],
  kind?: FileTypeKind,
): ContextualMenuItem[] {
  return actions
    .filter((action) => {
      if (!kind) return true
      if (action.hideForKinds?.includes(kind)) return false
      if (action.whenKinds && !action.whenKinds.includes(kind)) return false
      return true
    })
    .map((action) => {
      const dangerClass =
        action.tone === 'danger' ? ' ds-file-card__menu-danger' : ''
      const DsIcon = MENU_ICONS[action.id]
      let icon: React.ReactNode
      if (DsIcon) {
        icon = (
          <DsIcon
            className={`fal-menu-svg fal-menu-svg--${action.id}${dangerClass}`}
            aria-hidden
          />
        )
      } else if (action.icon) {
        icon = <i className={`mgc ${action.icon}${dangerClass}`} aria-hidden />
      }
      return {
        id: action.id,
        label: action.label,
        disabled: action.disabled,
        icon,
        onSelect: action.onClick,
      }
    })
}
